import _uniq from 'lodash/uniq';

import getThumbnailUrls from '../../helpers/getThumbnailUrls';
import store from '../index';
import Logger from '../../services/Logger';
import { getRoleById } from '../reducers/helpers/roles';
import ua from '../../ua';
import sdk from '../../sdk';
import * as helpers from '../helpers/notifications';
import TYPES from '../action-types';
import { addHighlight } from './assets';
import picsioConfig from '../../../../../config';
import { TAB_MENTIONS } from '../../components/Notifications/helpers/constants';
import getEventAssetIds from '../../components/Notifications/helpers/getEventAssetIds';
import isMentionsNotification from '../../components/Notifications/helpers/isMentionsNotification';
import isOwnNotificationHidden from '../../components/Notifications/helpers/isOwnNotificationHidden';

function setBadge(value) {
  if (window.cordova) {
    if (ua.isMobileApp() && ua.getPlatform() === 'ios' && value > 99) {
      window.cordova.plugins?.notification?.badge?.set('99+');
    } else {
      window.cordova.plugins?.notification?.badge?.set(value);
    }
  } else if (navigator.setAppBadge) {
    navigator.setAppBadge(value);
  } else if (navigator.setExperimentalAppBadge) {
    navigator.setExperimentalAppBadge(value);
  } else if (window.ExperimentalBadge) {
    window.ExperimentalBadge.set(value);
  }
}

function clearBadge() {
  if (window.cordova) {
    window.cordova.plugins?.notification?.badge?.clear();
  } else if (navigator.clearAppBadge) {
    navigator.clearAppBadge();
  } else if (navigator.clearExperimentalAppBadge) {
    navigator.clearExperimentalAppBadge();
  } else if (window.ExperimentalBadge) {
    window.ExperimentalBadge.clear();
  }
}

function setDocumentTitle(notificationsNumber) {
  const currentTitle = document.title;
  const index = currentTitle.indexOf(picsioConfig.isPim ? 'Toriut.com' : 'Pics.io');
  let title = currentTitle.substring(index);

  // if navigator support badges we need to receive count of unread messages
  if (
    navigator.clearAppBadge
    || navigator.setExperimentalAppBadge
    || window.ExperimentalBadge
    || window.cordova
  ) {
    if (notificationsNumber === 0) {
      clearBadge();
    }
    if (notificationsNumber > 0) {
      setBadge(notificationsNumber);
    }
  }

  const normalizedNotificationsNumber = notificationsNumber < 100 ? notificationsNumber : '99+';
  if (normalizedNotificationsNumber && !picsioConfig.isPim) title = `(${normalizedNotificationsNumber}) ${title}`;
  document.title = title;
}

/** Get notifications asset thumbnail */
export function setThumbnails(ids) {
  return async (dispatch) => {
    dispatch({
      type: TYPES.NOTIFICATIONS.GET_THUMBNAILS.START,
      payload: {
        ids,
      },
    });

    try {
      let thumbnails = await getThumbnailUrls(ids);
      if (thumbnails.some((item) => !item.thumbnailLink)) {
        thumbnails = await getThumbnailUrls(ids);
      }

      dispatch({
        type: TYPES.NOTIFICATIONS.GET_THUMBNAILS.COMPLETE,
        payload: {
          thumbnails,
        },
      });
    } catch (error) {
      Logger.error(new Error('Can not get notifications thumbnails'), { error }, [
        'NotificationsSetThumbnailsFailed',
        { errorMessage: (error && error.message) || 'NoMessage' },
      ]);
      dispatch({
        type: TYPES.NOTIFICATIONS.GET_THUMBNAILS.FAILED,
        error,
      });
    }
  };
}

function fetchNotificationCenter({ size = 50, from, tab } = {}) {
  if (sdk.users.axios) {
    return sdk.users.axios.get('/events/notificationCenter', { params: { size, from, tab } });
  }
  return sdk.users.fetchNotifications(size, { from, tab });
}

/** Receive notification center feed */
export function getNotifications({ tab, from } = {}) {
  return async (dispatch, getState) => {
    const append = Boolean(from);
    if (append && getState().notifications.isPageLoading) {
      return;
    }

    dispatch({
      type: TYPES.NOTIFICATIONS.FETCH.START,
      payload: { append },
    });
    let result = {};

    try {
      const { data } = await fetchNotificationCenter({
        size: helpers.NOTIFICATION_PAGE_SIZE,
        from,
        tab,
      });
      result = data || {};
      const { _id: currentUserId } = await getState().user;
      const team = await getState().teammates.items;
      const rawEvents = result.events || [];
      // Cursor and page size stay on the server page. Hiding own events must not
      // make a full page look like the last one.
      const receivedCount = rawEvents.length;
      const pageCursor = helpers.feedCursor(rawEvents) || null;

      const items = rawEvents.reduce((visible, item) => {
        if (isOwnNotificationHidden(item, currentUserId)) {
          return visible;
        }
        if (item.initiator) {
          const user = team.find((teammate) => teammate._id === item.initiator._id);
          if (user) {
            item.initiator.avatar = user.avatar;
            item.initiator.displayName = user.displayName;
          }
        }
        visible.push(item);
        return visible;
      }, []);
      const hiddenUnread = rawEvents.filter((item) => (
        !item.read && isOwnNotificationHidden(item, currentUserId)
      )).length;
      // The server count is the total, not this page. Adjust it once on the first
      // fetch. A later page must not replace the badge the user already has.
      const notificationsUnreadCount = append
        ? getState().notifications.notificationsUnreadCount
        : Math.max(0, (result.count || 0) - hiddenUnread);
      const { newComments, newRevisions } = helpers.collectUnreadHighlights(items);

      // The initiator's own unread failed full sync is hidden from the feed.
      // Detect it on the raw page so the dialog still runs, matching pubsub.
      // Skip already-read events, events without data, and other users. A missing
      // initiator must not throw.
      const fullSyncFailedEvent = rawEvents.find((item) => (
        item.type === 'sync.failed.FullSync'
        && item.data
        && item.read !== true
        && item.initiator?._id === currentUserId
      ));
      if (fullSyncFailedEvent) {
        const { code, websites } = fullSyncFailedEvent.data;
        helpers.showDestructiveSyncErrorDialog(code, websites, getState);
        notificationMarkAsRead(fullSyncFailedEvent._id)(dispatch, getState);
      }

      dispatch({
        type: TYPES.NOTIFICATIONS.FETCH.COMPLETE,
        payload: {
          items,
          append,
          receivedCount,
          pageCursor,
          newComments,
          newRevisions,
          notificationsUnreadCount,
        },
      });

      setDocumentTitle(append
        ? notificationsUnreadCount
        : (notificationsUnreadCount || items.filter((item) => !item.read).length));

      const itemsToThumbnailing = items.filter((item) => helpers.eventsWithThumbnails.includes(item.type));

      if (itemsToThumbnailing.length) {
        const ids = _uniq(itemsToThumbnailing.flatMap((item) => getEventAssetIds(item, 4)));
        if (ids.length) {
          setThumbnails(ids)(dispatch);
        }
      }
    } catch (error) {
      Logger.error(new Error('Can not get notifications'), { error }, [
        'NotificationsFetchFailed',
        { errorMessage: (error && error.message) || 'NoMessage' },
      ]);
      dispatch({
        type: TYPES.NOTIFICATIONS.FETCH.FAILED,
        error,
        payload: { append },
      });
    }
  };
}

/** Receive notification */
export function add(item) {
  return async (dispatch) => {
    const notifications = store.getState().notifications.items;
    if (notifications.find((notification) => notification._id === item._id)) return;

    dispatch({
      type: TYPES.NOTIFICATIONS.ADD,
      payload: { item: { ...item, read: false } },
    });

    setDocumentTitle(store.getState().notifications.notificationsUnreadCount);

    if (helpers.eventsWithThumbnails.includes(item.type)) {
      const ids = getEventAssetIds(item, 4);
      if (ids.length) {
        setThumbnails(ids)(dispatch);
      }
    }
  };
}

/** Receive IDS of changed collections */
export const addChangedTagsIds = (ids) => {
  const { teammateRoleId } = store.getState().user.team;
  const role = teammateRoleId && getRoleById(teammateRoleId);
  const allowedCollections = role && role.allowedCollections;
  if (allowedCollections) {
    const isIdsContainAllowedCollections = allowedCollections.some((collection) => ids.includes(collection._id));
    if (!isIdsContainAllowedCollections) {
      ids = [];
    }
  }

  return (dispatch) => {
    dispatch({
      type: TYPES.NOTIFICATIONS.ADD_CHANGED_TAGS_IDS,
      payload: { ids },
    });
  };
};

/** Mark notifications in the active tab as read */
export function markAllNotificationsAsRead(tab) {
  return async (dispatch, getState) => {
    const {
      items,
      notificationsUnreadCount,
      newComments = {},
      newRevisions = {},
    } = getState().notifications;
    const { _id: userId } = getState().user;
    const notificationIDS = items
      .filter((item) => !item.read && (tab !== TAB_MENTIONS || isMentionsNotification(item, userId)))
      .map((item) => item._id);

    dispatch({
      type: TYPES.NOTIFICATIONS.CLEAR.START,
      payload: { notificationIDS },
    });

    try {
      let data;
      if (sdk.users.axios) {
        ({ data } = await sdk.users.axios.put('/events/markAllAsRead', tab ? { tab } : {}));
      } else {
        const response = await sdk.users.notificationsMarkAllAsRead(tab);
        data = response?.data ?? response;
      }
      const computedRemaining = helpers.remainingUnreadAfterMarkAll(
        tab,
        notificationsUnreadCount,
        helpers.markedCountFromMarkAllResponse(data, notificationIDS.length),
      );
      const latestNotifications = getState().notifications;
      const remainingUnread = Math.min(
        latestNotifications.notificationsUnreadCount,
        computedRemaining,
      );
      setDocumentTitle(remainingUnread);
      dispatch({
        type: TYPES.NOTIFICATIONS.CLEAR.COMPLETE,
        payload: {
          notificationIDS,
          notificationsUnreadCount: remainingUnread,
          ...(tab === TAB_MENTIONS ? {
            committedNotificationIDS: helpers.committedMentionNotificationIds(
              items,
              latestNotifications.items,
              newComments,
              newRevisions,
              latestNotifications.newComments,
              latestNotifications.newRevisions,
              userId,
            ),
          } : {}),
        },
      });
    } catch (error) {
      Logger.error(new Error('Can not mark all notifications as read'), { error }, [
        'NotificationsMarkAllAsReadFailed',
        { errorMessage: (error && error.message) || 'NoMessage' },
      ]);
      dispatch({
        type: TYPES.NOTIFICATIONS.CLEAR.FAILED,
        error,
        payload: {
          notificationIDS,
          newComments,
          newRevisions,
        },
      });
    }
  };
}

let readRollbackSeq = 0;

function nextReadRollbackId(id) {
  readRollbackSeq += 1;
  return `${readRollbackSeq}-${id}`;
}

/** Read notification */
export function notificationMarkAsRead(notificationID, withoutChangeCount) {
  return async (dispatch, getState) => {
    const {
      items,
      newComments = {},
      newRevisions = {},
    } = getState().notifications;
    const current = items.find((item) => item._id === notificationID);
    if (current?.read) {
      return;
    }

    const notificationIDS = [notificationID];
    const rollbackId = nextReadRollbackId(notificationID);

    dispatch({
      type: TYPES.NOTIFICATIONS.MARK_AS_READ.START,
      payload: {
        notificationIDS,
        withoutChangeCount,
        rollbackId,
      },
    });

    if (!withoutChangeCount) {
      setDocumentTitle(getState().notifications.notificationsUnreadCount);
    }

    try {
      await sdk.users.notificationMarkAsRead(notificationID);
      dispatch({
        type: TYPES.NOTIFICATIONS.MARK_AS_READ.COMPLETE,
        payload: {
          withoutChangeCount,
          notificationIDS,
          rollbackId,
        },
      });
    } catch (error) {
      Logger.error(new Error('Can not mark notification as read'), { error }, [
        'NotificationMarkAsReadFailed',
        { errorMessage: (error && error.message) || 'NoMessage' },
      ]);
      dispatch({
        type: TYPES.NOTIFICATIONS.MARK_AS_READ.FAILED,
        error,
        payload: {
          notificationIDS,
          newComments,
          newRevisions,
          withoutChangeCount,
          rollbackId,
        },
      });
      if (!withoutChangeCount) {
        setDocumentTitle(getState().notifications.notificationsUnreadCount);
      }
    }
  };
}

/** Read notifications */
export const notificationsMarkAsRead = (assetId, withoutChangeCount) => async (dispatch) => {
  const { newComments, newRevisions, items } = store.getState().notifications;
  const unredNewCommentsEventIds = newComments[assetId] || [];
  const unredNewRevisionsEventIds = newRevisions[assetId] || [];
  const notificationIDS = helpers.markableNotificationIds(
    items,
    _uniq([...unredNewCommentsEventIds, ...unredNewRevisionsEventIds]),
  );
  if (!notificationIDS.length) return true;

  const rollbackId = nextReadRollbackId(assetId);

  dispatch({
    type: TYPES.NOTIFICATIONS.MARK_AS_READ.START,
    payload: {
      notificationIDS,
      withoutChangeCount,
      rollbackId,
    },
  });

  if (!withoutChangeCount) {
    setDocumentTitle(store.getState().notifications.notificationsUnreadCount);
  }

  try {
    await sdk.users.notificationsMarkAsRead(notificationIDS);
    dispatch({
      type: TYPES.NOTIFICATIONS.MARK_AS_READ.COMPLETE,
      payload: {
        withoutChangeCount,
        notificationIDS,
        rollbackId,
      },
    });
  } catch (error) {
    Logger.error(new Error('Can not mark notification as read'), { error }, [
      'NotificationMarkAsReadFailed',
      { errorMessage: (error && error.message) || 'NoMessage' },
    ]);
    dispatch({
      type: TYPES.NOTIFICATIONS.MARK_AS_READ.FAILED,
      error,
      payload: {
        notificationIDS,
        newComments: newComments || {},
        newRevisions: newRevisions || {},
        withoutChangeCount,
        rollbackId,
      },
    });
    if (!withoutChangeCount) {
      setDocumentTitle(store.getState().notifications.notificationsUnreadCount);
    }
  }
};

/** Clear IDs of changed collections */
export const clearChangedTagsIds = () => ({
  type: TYPES.NOTIFICATIONS.CLEAR_CHANGED_TAGS_IDS,
});

/** Fetch async jobs status */
export function fetchJobsStatus() {
  return async (dispatch) => {
    dispatch({ type: TYPES.NOTIFICATIONS.FETCH_JOBS.START });

    try {
      const { data } = await sdk.users.fetchJobsStatus();
      const jobsStatus = { ...data };
      jobsStatus.importing = jobsStatus?.collections?.importing + jobsStatus.importing;
      dispatch({
        type: TYPES.NOTIFICATIONS.FETCH_JOBS.COMPLETE,
        payload: {
          jobsStatus,
        },
      });
    } catch (error) {
      /* if browser tab is hidden
       * maybe the browser has terminated the request
       */
      dispatch({
        type: TYPES.NOTIFICATIONS.FETCH_JOBS.FAILED,
        error,
      });
    }
  };
}

export function updateJobsStatus(data) {
  const jobsStatus = { ...data };
  jobsStatus.importing = jobsStatus?.collections?.importing + jobsStatus.importing;
  return async (dispatch) => {
    dispatch({
      type: TYPES.NOTIFICATIONS.FETCH_JOBS.COMPLETE,
      payload: { jobsStatus },
    });
  };
}

export function updateUnreadParam(paramName, eventId, assetId) {
  return async (dispatch) => {
    addHighlight([assetId], paramName)(dispatch);

    dispatch({
      type: TYPES.NOTIFICATIONS.INCREASE_UNREAD_PARAM,
      payload: { paramName, eventId, assetId },
    });
  };
}
